import { useState, useMemo } from 'react';
import { useCrawls } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader, type SortDir } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { formatNumber, timeAgo, formatDuration } from '@/lib/utils';
import { History } from 'lucide-react';

const statusVariant = (status: string): 'success' | 'info' | 'warning' | 'destructive' | 'secondary' => {
  const map: Record<string, 'success' | 'info' | 'warning' | 'destructive' | 'secondary'> = {
    completed: 'success', running: 'info', pending: 'warning', failed: 'destructive',
  };
  return map[status] ?? 'secondary';
};

const statusDot = (status: string) => (
  <Badge variant={statusVariant(status)} className="text-xs capitalize">{status}</Badge>
);

export default function CrawlsPage() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('started_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
      setPage(1);
    }
  };

  const { data, isLoading } = useCrawls({ page, sort: sortField, order: sortDir });

  // Status summary
  const statusSummary = useMemo(() => {
    if (!data?.crawlRuns) return {};
    const counts: Record<string, number> = {};
    data.crawlRuns.forEach(r => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [data?.crawlRuns]);

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="banner-chip">
            <h1 className="text-2xl font-bold banner-title">Crawl Runs</h1>
          </div>
          {data && (
            <div className="banner-chip">
              <Badge variant="outline">{data.total} total runs</Badge>
            </div>
          )}
        </div>

        {/* Status Summary */}
        {Object.keys(statusSummary).length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(statusSummary).map(([status, count]) => (
              <div key={status} className="banner-chip">
                <Badge
                  variant={status === 'completed' ? 'success' : status === 'failed' ? 'destructive' : status === 'running' ? 'info' : 'secondary'}
                  className="text-sm px-3 py-1"
                >
                  {count} {status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5 text-blue-700 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-400">All Crawl Runs</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Loading crawl runs..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>
                      <SortableHeader label="Source" field="county_name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Status" field="status" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Found" field="parcels_found" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="New" field="parcels_new" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Errors" field="parcels_errors" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Duration" field="duration_secs" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Started" field="started_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>Completed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.crawlRuns.map((run, idx) => (
                    <TableRow
                      key={run.id}
                      className={`${idx % 2 === 0 ? '' : 'bg-table-row-even'} ${run.status === 'failed' ? 'bg-red-50 dark:bg-red-950/20' : ''}`}
                      style={{
                        borderLeft: run.status === 'completed' ? '3px solid #34d399'
                          : run.status === 'failed' ? '3px solid #f87171'
                          : run.status === 'running' ? '3px solid #60a5fa'
                          : '3px solid transparent',
                      }}
                    >
                      <TableCell className="font-mono text-xs">{run.country || 'US'}</TableCell>
                      <TableCell className="font-medium">{run.state_abbr?.trim()} / {run.county_name}</TableCell>
                      <TableCell>{statusDot(run.status)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(run.parcels_found)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(run.parcels_new)}</TableCell>
                      <TableCell className="text-right">
                        {(run.parcels_errors ?? 0) > 0
                          ? <Badge variant="destructive" className="text-xs">{run.parcels_errors}</Badge>
                          : <span className="font-mono text-muted-foreground">0</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatDuration(run.duration_secs)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{timeAgo(run.started_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{run.completed_at ? timeAgo(run.completed_at) : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {data?.crawlRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                        No crawl runs yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePager
                page={page}
                onPageChange={setPage}
                total={data?.total}
                pageSize={data?.limit ?? 20}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
